const TYPE_TITLES = {
  NEW_REQUEST: 'New Expense Request',
  REQUEST_APPROVED: 'Expense Request Approved',
  REQUEST_DECLINED: 'Expense Request Declined'
};

const TYPE_MESSAGE_FALLBACKS = {
  NEW_REQUEST: (data) => {
    const who = data?.requester || data?.userName || data?.user || null;
    const amount = data?.amount ? ` for ${data.amount}` : '';
    return `New expense request${who ? ` from ${who}` : ''}${amount}`;
  },
  REQUEST_APPROVED: (data) => {
    const id = data?.expense_request_id || data?.requestId || data?.request_id;
    return `Expense request${id ? ` #${id}` : ''} has been approved`;
  },
  REQUEST_DECLINED: (data) => {
    const id = data?.expense_request_id || data?.requestId || data?.request_id;
    const reason = data?.declineReason || data?.reason;
    if (id && reason) return `Expense request #${id} was declined: ${reason}`;
    if (id) return `Expense request #${id} was declined`;
    return reason ? `Expense request was declined: ${reason}` : 'Expense request was declined';
  }
};

function parseNotificationData(data) {
  if (!data) return null;
  if (typeof data === 'object') return data;
  if (typeof data === 'string') {
    try {
      return JSON.parse(data);
    } catch {
      return data; // Keep original string if it isn't valid JSON
    }
  }
  return null;
}

function normalizeNotification(notification) {
  if (!notification) {
    return {
      id: Date.now(),
      title: 'Notification',
      message: '',
      is_read: true
    };
  }

  const parsedData = parseNotificationData(notification.data);
  const dataObject = typeof parsedData === 'object' ? parsedData : null;

  const normalized = {
    ...notification,
    data: parsedData
  };

  // Prefer richer title/message from the payload data if present
  const dataTitle = dataObject?.title || dataObject?.notificationTitle;
  const dataMessage = dataObject?.message || dataObject?.body || dataObject?.text || dataObject?.notification;

  const typeTitle = TYPE_TITLES[notification.type];
  const typeMessageFallback = TYPE_MESSAGE_FALLBACKS[notification.type]?.(dataObject || {});

  normalized.title = dataTitle || notification.title || typeTitle || 'Notification';
  normalized.message = dataMessage || notification.message || typeMessageFallback || '';

  // Normalize common identifiers/flags we use elsewhere in the UI
  normalized.expense_request_id = notification.expense_request_id
    || dataObject?.expense_request_id
    || dataObject?.requestId
    || dataObject?.request_id;

  normalized.is_read = typeof notification.is_read === 'boolean'
    ? notification.is_read
    : !!(notification.read_at || notification.readAt || notification.read);

  normalized.created_at = notification.created_at
    || notification.createdAt
    || notification.timestamp
    || notification.timeStamp
    || dataObject?.created_at
    || dataObject?.createdAt;

  return normalized;
}

export default {
  namespaced: true,
  name: 'NOTIFICATION_MODULE',
  state: {
    notifications: [],
    unreadCount: 0,
    loading: false,
    error: null
  },
  getters: {
    getAllNotifications: state => state.notifications || [],
    getUnreadNotifications: state => (state.notifications || []).filter(n => !n.is_read),
    getReadNotifications: state => (state.notifications || []).filter(n => n.is_read),
    getUnreadCount: state => state.unreadCount || 0,
    notificationLoading: state => state.loading,
    notificationError: state => state.error
  },
  mutations: {
    SET_NOTIFICATIONS(state, notifications) {
      const normalized = (notifications || []).map(normalizeNotification);
      state.notifications = normalized.sort((a, b) => {
        const aTime = new Date(a.created_at || a.timestamp || 0).getTime();
        const bTime = new Date(b.created_at || b.timestamp || 0).getTime();
        if (aTime === bTime) return (b.id || 0) - (a.id || 0);
        return bTime - aTime;
      });
    },
    SET_UNREAD_COUNT(state, count) {
      state.unreadCount = count;
    },
    ADD_NOTIFICATION(state, notification) {
      const normalized = normalizeNotification(notification);
      state.notifications.unshift(normalized);
      state.notifications.sort((a, b) => {
        const aTime = new Date(a.created_at || a.timestamp || 0).getTime();
        const bTime = new Date(b.created_at || b.timestamp || 0).getTime();
        if (aTime === bTime) return (b.id || 0) - (a.id || 0);
        return bTime - aTime;
      });
      if (!normalized.is_read) {
        state.unreadCount++;
      }
    },
    MARK_AS_READ(state, notificationId) {
      const notification = state.notifications.find(n => n.id === notificationId);
      if (notification && !notification.is_read) {
        notification.is_read = true;
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
    },
    MARK_ALL_AS_READ(state) {
      state.notifications.forEach(n => {
        n.is_read = true;
      });
      state.unreadCount = 0;
    },
    REMOVE_NOTIFICATION(state, notificationId) {
      const index = state.notifications.findIndex(n => n.id === notificationId);
      if (index !== -1) {
        const notification = state.notifications[index];
        if (!notification.is_read) {
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
        state.notifications.splice(index, 1);
      }
    },
    SET_LOADING(state, loading) {
      state.loading = loading;
    },
    SET_ERROR(state, error) {
      state.error = error;
    },
    CLEAR_ERROR(state) {
      state.error = null;
    }
  },
  actions: {
    async fetchNotifications(context, userId) {
      try {
        context.commit('SET_LOADING', true);
        context.commit('CLEAR_ERROR');

        const url = `${window.pettyCashApi}api/notifications`;

        const response = await axios.get(url, {
          params: { userId }
        });

        // Handle both possible response formats
        let notifications = [];
        if (response.data && Array.isArray(response.data)) {
          // Direct array response
          notifications = response.data;
        } else if (response.data && Array.isArray(response.data.data)) {
          // Wrapped in data property
          notifications = response.data.data;
        } else {
          notifications = [];
        }

        context.commit('SET_NOTIFICATIONS', notifications);

        // Calculate and set unread count
        const normalizedNotifications = context.state.notifications || [];
        const unreadCount = normalizedNotifications.filter(n => !n.is_read).length;
        context.commit('SET_UNREAD_COUNT', unreadCount);

        return normalizedNotifications;
      } catch (error) {
        const isOffline = (typeof navigator !== 'undefined' && navigator.onLine === false)
          || !error?.response
          || (error?.message || '').toLowerCase().includes('network');
        context.commit('SET_ERROR', error.message || 'An error occurred while fetching notifications');
        if (!isOffline && window.errorHandler && window.errorHandler.tomcatError) {
          window.errorHandler.tomcatError(error);
        }
        if (!isOffline) {
          throw error;
        }
        return [];
      } finally {
        context.commit('SET_LOADING', false);
      }
    },

    async fetchUnreadCount(context, userId) {
      try {
        const url = `${window.pettyCashApi}api/notifications/unread-count`;

        const response = await axios.get(url, {
          params: { userId }
        });

        if (response.data.success) {
          context.commit('SET_UNREAD_COUNT', response.data.count);
          return response.data.count;
        } else {
          throw new Error(response.data.message || 'Failed to fetch unread count');
        }
      } catch (error) {
        context.commit('SET_ERROR', error.message || 'An error occurred while fetching unread count');
        window.errorHandler.tomcatError(error);
        throw error;
      }
    },

    async markAsRead(context, notificationId) {
      try {
        const url = `${window.pettyCashApi}api/notifications/${notificationId}/read`;

        const response = await axios.put(url);

        if (response.data.success) {
          context.commit('MARK_AS_READ', notificationId);
          return true;
        } else {
          throw new Error(response.data.message || 'Failed to mark notification as read');
        }
      } catch (error) {
        context.commit('SET_ERROR', error.message || 'An error occurred while marking notification as read');
        window.errorHandler.tomcatError(error);
        throw error;
      }
    },

    async markAllAsRead(context, userId) {
      try {
        const url = `${window.pettyCashApi}api/notifications/read-all`;

        const response = await axios.put(url, null, {
          params: { userId }
        });

        if (response.data.success) {
          context.commit('MARK_ALL_AS_READ');
          return true;
        } else {
          throw new Error(response.data.message || 'Failed to mark all notifications as read');
        }
      } catch (error) {
        context.commit('SET_ERROR', error.message || 'An error occurred while marking all notifications as read');
        window.errorHandler.tomcatError(error);
        throw error;
      }
    },

    async deleteNotification(context, notificationId) {
      try {
        const url = `${window.pettyCashApi}api/notifications/${notificationId}`;

        const response = await axios.delete(url);

        if (response.data.success) {
          context.commit('REMOVE_NOTIFICATION', notificationId);
          return true;
        } else {
          throw new Error(response.data.message || 'Failed to delete notification');
        }
      } catch (error) {
        context.commit('SET_ERROR', error.message || 'An error occurred while deleting notification');
        window.errorHandler.tomcatError(error);
        throw error;
      }
    },

    addNewNotification(context, notification) {
      context.commit('ADD_NOTIFICATION', notification);
    }
  }
};
